import { useState } from 'react';
import { Scale, ExternalLink, AlertTriangle, FileText, Shield } from 'lucide-react';

/**
 * PublicRecords section
 *
 * Renders the lawsuit / news / enforcement findings from the LLM web search.
 * Appears between the narrative and the crime backdrop in the report. This
 * is the strategic differentiator — the signal that crime data feeds CAN'T
 * surface but courts and news do.
 *
 * Each finding shows:
 *   - Type icon (lawsuit, news, enforcement, settlement, licensing)
 *   - Headline
 *   - Details (1-2 sentence summary)
 *   - Source link (opens in new tab)
 *   - Date if known
 *   - Confidence + address-match-quality badges
 *
 * If the search ran and found nothing, shows a clean "no records found"
 * message that's actually informative (states what was searched).
 */
export default function PublicRecords({ data }) {
  const [expandedIdx, setExpandedIdx] = useState(null);

  if (!data) return null;

  // Search ran, no findings — still show the section as positive signal
  if (data.available && (!data.findings || data.findings.length === 0)) {
    return (
      <div className="card mb-6 animate-fade-in-up" style={{ animationDelay: '0.45s', animationFillMode: 'both' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Scale size={18} className="text-teal" />
            <h2 className="section-title mb-0">Public records search</h2>
          </div>
          <div className="text-xs text-green-700 font-bold flex items-center gap-1">
            <Shield size={12} />
            No records found
          </div>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Searched public sources (court filings, news coverage, enforcement actions, licensing
          records) for trafficking-related public records at this property. No matching records
          were found in indexed sources.
        </p>
        {data.coverage_note && (
          <p className="text-xs text-gray-400 italic mt-2">{data.coverage_note}</p>
        )}
      </div>
    );
  }

  // Search didn't run — show a brief gray placeholder, not alarming
  if (!data.available) {
    return null;
  }

  // Findings exist — render the full list
  const findings = data.findings || [];

  return (
    <div className="card mb-6 animate-fade-in-up" style={{ animationDelay: '0.45s', animationFillMode: 'both' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Scale size={18} className="text-amber-600" />
          <h2 className="section-title mb-0">Public records</h2>
        </div>
        <div className="text-xs font-bold text-amber-700">
          {findings.length} record{findings.length === 1 ? '' : 's'} found
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-4 max-w-3xl">
        Lawsuits, news coverage, and enforcement actions identified for this property through
        public records search. Each finding is sourced — click to view the underlying article
        or filing. Underwriter verification recommended before binding.
      </p>

      <div className="space-y-3">
        {findings.map((f, idx) => (
          <FindingRow
            key={idx}
            finding={f}
            expanded={expandedIdx === idx}
            onToggle={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
          />
        ))}
      </div>
    </div>
  );
}

function FindingRow({ finding, expanded, onToggle }) {
  const TypeIcon = typeIcon(finding.type);
  const colors = typeColors(finding.type);

  return (
    <div className={`border rounded-lg overflow-hidden ${colors.border} ${colors.bg}`}>
      <button
        onClick={onToggle}
        className="w-full text-left px-4 py-3 hover:bg-white/50 transition"
      >
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 mt-0.5 ${colors.icon}`}>
            <TypeIcon size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className={`text-xs font-bold uppercase tracking-wider ${colors.icon}`}>
                {finding.type}
                {finding.date && (
                  <span className="text-gray-500 ml-2 font-normal normal-case">{finding.date}</span>
                )}
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <ConfidenceBadge confidence={finding.confidence} />
                <MatchBadge match={finding.address_match_quality} />
              </div>
            </div>
            <div className={`text-sm font-semibold ${colors.text}`}>
              {finding.headline}
            </div>
            {finding.amount_usd && (
              <div className={`text-xs font-bold mt-1 ${colors.text}`}>
                ${(finding.amount_usd / 1_000_000).toFixed(1)}M
              </div>
            )}
            {finding.details && (
              <div className="text-sm text-gray-600 mt-1.5 leading-relaxed">
                {finding.details}
              </div>
            )}
          </div>
        </div>
      </button>

      {expanded && (finding.source_url || finding.source_name) && (
        <div className="px-4 pb-3 pt-1 border-t border-gray-100 mt-2">
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <span>Source:</span>
            {finding.source_url ? (
              <a
                href={finding.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal hover:text-teal-dark font-medium inline-flex items-center gap-1"
              >
                {finding.source_name || finding.source_url}
                <ExternalLink size={11} />
              </a>
            ) : (
              <span className="font-medium">{finding.source_name}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ConfidenceBadge({ confidence }) {
  const colors = confidence === 'high'
    ? 'bg-green-100 text-green-700'
    : confidence === 'medium'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-gray-100 text-gray-600';

  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${colors}`}>
      {confidence} conf
    </span>
  );
}

function MatchBadge({ match }) {
  const colors = match === 'exact'
    ? 'bg-green-100 text-green-700'
    : match === 'strong'
      ? 'bg-blue-100 text-blue-700'
      : 'bg-gray-100 text-gray-600';

  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${colors}`}>
      {match} match
    </span>
  );
}

function typeIcon(type) {
  switch (type) {
    case 'lawsuit':
    case 'settlement':
      return Scale;
    case 'enforcement':
      return AlertTriangle;
    case 'licensing':
      return Shield;
    case 'news':
    default:
      return FileText;
  }
}

function typeColors(type) {
  switch (type) {
    case 'lawsuit':
    case 'settlement':
      return {
        border: 'border-red-200',
        bg: 'bg-red-50/50',
        icon: 'text-red-600',
        text: 'text-red-900'
      };
    case 'enforcement':
      return {
        border: 'border-orange-200',
        bg: 'bg-orange-50/50',
        icon: 'text-orange-600',
        text: 'text-orange-900'
      };
    case 'licensing':
      return {
        border: 'border-amber-200',
        bg: 'bg-amber-50/50',
        icon: 'text-amber-700',
        text: 'text-amber-900'
      };
    case 'news':
    default:
      return {
        border: 'border-gray-200',
        bg: 'bg-gray-50/50',
        icon: 'text-gray-700',
        text: 'text-navy'
      };
  }
}
